import React, { useState, useRef, useEffect } from "react";
import CreatableSelect from "react-select/creatable";
import { withStyles } from "@material-ui/core/styles";
import createStyles from "@material-ui/core/styles/createStyles";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import EditIcon from "@material-ui/icons/Edit";
import BrushIcon from "@material-ui/icons/Brush";
import { lighten } from "@material-ui/core/styles/colorManipulator";
import withContext from "../../tools/withContext";
import TagListUpdateDialog from "./TagListUpdateDialog";
import { StyledTooltip } from "./StyledTooltip";

const styles = theme =>
  createStyles({
    labels: {
      padding: `0 ${theme.spacing.unit * 2}px`,
    },
    labelsModern: {
      padding: 0,
    },
    padded: {
      padding: 5,
    },
    selectRow: {
      marginBottom: 8,
    },
  });

const ActionIcon = ({ title, disabled, onClick, color, component: IconComponent }) => (
  <StyledTooltip title={title} placement="top">
    <span>
      <IconButton
        disabled={disabled}
        onClick={onClick}
        style={{ padding: 4, margin: "0 1px" }}
      >
        <IconComponent style={{ color: color || undefined }} />
      </IconButton>
    </span>
  </StyledTooltip>
);

const LabelsFromList = props => {
  const {
    labels,
    images,
    saveLabel,
    saveImage,
    updateLabels,
    selected,
    noImage,
    classes,
    onSelect,
    withActions = false,
    frequentLabels,
    frequentLabelsNumber,
    variant = "default",
    ctx: { user },
  } = props;

  const selectRef = useRef(null);
  const [newTags, setNewTags] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const isModern = variant === "segmentation2";

  const handleChange = (newValue, actionMeta) => {
    if (actionMeta.action === "create-option") {
      saveLabel(newValue.label).then(indexNewTag => {
        onSelect(indexNewTag);
        setNewTags(newTags.concat([newValue.label]));
      });
    }
    if (actionMeta.action === "select-option") {
      onSelect(labels.findIndex(l => l.legend === newValue.label));
    }
  };

  const handleUpdateTags = tagMap => updateLabels(tagMap);

  const selectedValue =
    selected === null || typeof selected === "undefined" || !labels[selected]
      ? null
      : labels[selected].legend;

  useEffect(() => {
    if (selectRef && selected === null) {
      selectRef.current.focus();
    }
  }, [noImage, selected]);

  const handleClose = () => setDialogOpen(false);
  const handleOpen = () => setDialogOpen(true);

  const frequentLabelItems = Object.keys(frequentLabels)
    .map(k => ({ legend: k, nombre: frequentLabels[k] }))
    .filter(l => l.nombre > 0)
    .sort((l1, l2) => {
      if (l2.nombre > l1.nombre) return 1;
      if (l2.nombre < l1.nombre) return -1;
      return 0;
    })
    .slice(0, frequentLabelsNumber - 1);

  const renderModernFrequentLabel = (label, index) => {
    if (!labels[index]) return null;

    const isSelected = index === selected;
    const labelColor = labels[index].color || "#ccc";    

    return (
      <Grid item key={index} xs={6}>
        <Grid container>
          <Grid item xs={12}>
            <Paper
              style={{
                backgroundColor: lighten(labelColor, isSelected ? 0.6 : 0.98),
                border: `2px solid ${lighten(labelColor, isSelected ? 0 : 0.5)}`,
                padding: "8px",
                borderRadius: "10px",
                cursor: "pointer",
              }}
              onClick={() => onSelect(index)}
            >
              <Grid container alignItems="center">
                <Grid item xs={11}>
                  <Typography>{label.legend}</Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          {withActions && (
            <Grid
              item
              container
              xs={12}
              justify="flex-end"
              style={{
                marginBottom: "8px",
                height: 40,
                padding: "4px 2px",
              }}
            >
              <ActionIcon
                title="Labelliser"
                disabled={false}
                color={isSelected ? labelColor : ""}
                component={BrushIcon}
                onClick={e => {
                  e.stopPropagation();
                  onSelect(index);
                }}
              />
            </Grid>
          )}

        </Grid>
      </Grid>
    );
  };

  const renderDefaultFrequentLabel = (label, index) => {
    if (!labels[index]) return null;

    const color = lighten(labels[index].color, index === selected ? 0 : 0.5);

    return (
      <Grid
        item
        key={index}
        xs={6}
        className={classes.padded}
        onClick={() => onSelect(index)}
      >
        <Grid
          container
          direction="row"
          justify="space-between"
          alignItems="center"
          style={{
            border: `solid 3px ${color}`,
            borderLeft: `solid ${index === selected ? "10px" : "3px"} ${color}`,
            borderRadius: "3px",
          }}
        >
          <Grid
            item
            xs={12}
            style={{
              paddingLeft: index === selected ? 5 : 9,
              fontSize: 16,
              padding: "5px",
            }}
          >
            {label.legend}
          </Grid>
        </Grid>
      </Grid>
    );
  };

  return (
    <Grid
      container
      direction="row"
      spacing={8}
      justify="space-between"
      alignItems="center"
      className={isModern ? classes.labelsModern : classes.labels}
    >
      <TagListUpdateDialog
        open={dialogOpen}
        onClose={handleClose}
        labels={newTags}
        images={images.filter(image =>
          newTags.includes(image.labels.simpleLabels[0])
        )}
        updateTags={handleUpdateTags}
        saveImage={saveImage}
      />
      <Grid
        item
        key="select"
        xs={12}
        className={isModern ? classes.selectRow : classes.padded}
      >
        <Grid
          container
          direction="row"
          justify="space-between"
          alignItems="center"
        >
          <Grid item xs={11}>
            <CreatableSelect
              isClearable={false}
              onChange={handleChange}
              options={labels.map(l => ({ value: l.legend, label: l.legend }))}
              formatCreateLabel={inputValue => `Créer le label ${inputValue}`}
              value={{ label: selectedValue, value: selectedValue }}
              autoFocus
              ref={selectRef}
              isValidNewOption={() => true}
            />
          </Grid>
          <Grid item xs={1}>
            <StyledTooltip
              title={
                newTags.length === 0
                  ? ""
                  : "Les nouveaux tags peuvent encore être modifiés"
              }
              placement="top"
            >
              <span>
                <IconButton
                  onClick={handleOpen}
                  disabled={newTags.length === 0}
                  style={{ padding: 4 }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </StyledTooltip>
          </Grid>
        </Grid>
      </Grid>
      {frequentLabelItems.map(label => {
        const index = labels.findIndex(l => l.legend === label.legend);
        return isModern
          ? renderModernFrequentLabel(label, index)
          : renderDefaultFrequentLabel(label, index);
      })}
    </Grid>
  );
};

LabelsFromList.defaultProps = {
  hideVisibilityIcon: false,
  variant: "default",
};

export default withStyles(styles)(withContext(LabelsFromList));
